import { useEffect, useState } from 'react';
import axios from 'axios';
import utils from '../../utils/utils';
import MODULE_SUB_MODULE_OPERATIONS_TREE from '../../const/ModuleSubModuleOperationsTree';
import useConsole from '../Console/useConsole';
import useDatasetGrid from '../useDatasetGrid';
import useProject from '../Project/useProject';
import ApiEndpoints from '../../const/ApiEndpoints';
import useDataIngestion from '../DM/useDataIngestion';
import useModelMetaData from '../useModelMetaData';
import { DATASET_REQUIRED_FIELDS } from '../../const/CommonConst';

const BASE_URL = ApiEndpoints.MODULE_BASE_URL.EDA;
const totalPageCount = 'x-total-count';

/**
 * @function useMissingValuesImputation
 * @description contains all the API calls, functions and
 * common states data for missing value imputation submodule.
 * @param {function} fetchModuleTasks - function for fetching module tasks
 * @param {array} datasetList - list of all the datasets
 * @param {function} fetchDatasetList - function for fetching dataset list
 * @param {function} fetchDatasetRowsWithPagination - function for fetching dataset rows with pagination
 * @param {array} datasetRowsWithPagination - list of all the dataset rows with pagination
 * @param {function} setDatasetRowsWithPagination - function for setting dataset rows with pagination
 * @param {number} totalDatasetRows - total number of dataset rows
 * @param {number} datasetRowsPageCount - number of dataset rows per page
 * @param {boolean} isLoading - boolean value to determine when to show and hide the loader
 * @param {function} updateDatasetCells - function for updating dataset cells
 * @param {object} project - object containing data like project id and project name
 * @returns {object} -returning multiple methods and states for missing value imputation submodule
 * @example const {fetchModuleTasks,fetchDatasetRowsWithPagination,
 * datasetList}=useMissingValuesImputation()
 */

export default function useMissingValuesImputation() {
  const {
    fetchModuleTasks,
    datasetList,
    fetchDatasetList,
    fetchDatasetRowsWithPagination,
    datasetRowsWithPagination,
    setDatasetRowsWithPagination,
    totalDatasetRows,
    datasetRowsPageCount,
    isLoading,

    updateDatasetCells,
    project,
  } = useProject();

  const {
    renameColumn,
    deleteColumn,
    duplicateColumn,
    addColumn,
    deleteRow,
    duplicateRow,
    addRow,
    tasks: gridTasks,
    taskCount: gridTaskCount,
    fetchTasks: fetchGridTasks,
    terminateTask: terminateGridTask,
    manageSpaces,
    manageCases,
    splitColumn,
    findAndReplace,
    sortColumn,
    analyse,
    embeddedValues,
    standardisationError,
    integrityRules,
    uniqueness,
    scores,
    fetchGridTaskData,
    findDuplicateRows,
    findDuplicateColumns,
    findAndDeleteDuplicateRows,
    findAndDeleteDuplicateColumns,
    setGridTaskResultsData,
    gridTaskResultsData,
    resetGridTaskResultsData,
    markTaskReadUnreadGrid,
    valueError,
    findBlankValuePercentage,
    replaceBlankValues,
    findRegexPercentage,
    gridOperationRerun,
  } = useDatasetGrid();

  const {
    consoleModuleTasks,
    setConsoleModuleTasks,
    consoleAsyncTaskResults,
    fetchAsyncResultConsole,
    terminateTaskConsole,
    markTaskReadUnreadConsole,
    fetchModuleTasksConsole,
    fetchUdfs,
    deleteUdf,
    createUdf,
    createJob,
    updateUdf,
    udfs,
    selectedUdf,
    setSelectedUdf,
    runUdf,
    githubIntegration,
    fetchUdfDetails,
    fetchDatasetColumnDetailsForConsole,
    setConsoleAsyncTaskResults,
    fetchAllModels,
    fetchTableData,
    onDownloadCSV,
    fetchConsoleLibraries,
    consoleRerunOperation,
    fetchConsoleFileList,
  } = useConsole();
  const { markDatasetAsReadyToUse } = useDataIngestion();

  const { addResultToModelMetaData, fetchModelMetaDataSchema } =
    useModelMetaData();

  // For Active Dataset
  const [activeDataset, setActiveDataset] = useState('');

  // For Column Details of Active Dataset
  const [datasetColumnDetails, setDatasetColumnDetails] = useState([]);

  // Missing Values Tasks
  const [moduleTasks, setModuleTasks] = useState([]);
  const [totalModuleTask, setTotalModuleTask] = useState(0);
  const [totalModuleTaskConsole, setTotalModuleTaskConsole] = useState(0);
  const [taskCount, setTaskCount] = useState({});

  // For Response Data : Graphs or Tables or Table ID
  const [taskDetails, updateTaskDetails] = useState({});

  useEffect(() => {
    if (activeDataset?.id) {
      datasetList.forEach((data) => {
        if (data.id === activeDataset.id) {
          setActiveDataset(data);
        }
      });
    }
  }, [datasetList]);

  const fetchAsyncResultSplit = (taskID, type) =>
    axios
      .get(`${BASE_URL + ApiEndpoints.EDA.GET_EDA_TASKS}/${taskID}/${type}/`)
      .then((res) => {
        return res;
      });

  // For Active Operation
  const [activeOperation, setActiveOperation] = useState(
    MODULE_SUB_MODULE_OPERATIONS_TREE.EDA.subModules.MVI.operations.MVI
  );

  /**
	@function resetTaskDetails
	@description used for updating the state of taskDetails
	@returns {undefined}
	@example resetTaskDetails()
	*/

  // reset data on Operation and Dataset Change
  const resetTaskDetails = () => {
    updateTaskDetails({});
  };

  /**
   * @function fetchDatasetColumnDetails
   * @description API call for fetching dataset column details
   * @param {object} datasetName - object containing data like table name and project id
   * @param {function} successCallback - callback executed when promise get resolved
   * @returns {Promise} Axios Promise
   * @example fetchDatasetColumnDetails(datasetName, successCallback);
   */

  // For fetching Active Dataset Columns
  const fetchDatasetColumnDetails = (datasetName, successCallback) =>
    axios
      .post(`${BASE_URL + ApiEndpoints.EDA.EDA_MVI_GET_COLUMNS}`, datasetName)
      .then(
        (res) => {
          setDatasetColumnDetails(res.data?.Columnlist);
          successCallback(res?.data);
        },
        () => {}
      );

  /**
   * @function fetchMVIModuleTasks
   * @description API call for fetching Missing Value Imputation module's all tasks
   * @param {number} projectId - project id of the selected project
   * @param {boolean} hideLoaderOnRequest - boolean value to determine when
   * to show and hide the loader.
   * @returns {undefined}
   * @example fetchMVIModuleTasks(projectId,hideLoaderOnRequest);
   */

  // For Fetaching the Tasks List
  const fetchMVIModuleTasks = (
    projectId,
    hideLoaderOnRequest,
    pageIndex,
    pageSize,
    taskStatus
  ) => {
    if (projectId) {
      return fetchModuleTasks(
        MODULE_SUB_MODULE_OPERATIONS_TREE.EDA.objKey,
        MODULE_SUB_MODULE_OPERATIONS_TREE.EDA.subModules.MVI.key,
        'GET_EDA_TASKS',
        () => {},
        projectId,
        hideLoaderOnRequest,
        '',
        pageIndex,
        pageSize,
        taskStatus,
        false,
        MODULE_SUB_MODULE_OPERATIONS_TREE.EDA.key
      ).then((res) => {
        if (res.data) {
          setTotalModuleTask(res.headers?.[totalPageCount]);
          if (pageIndex > 1) {
            setModuleTasks((prevModuleTasks) => [
              ...prevModuleTasks,
              ...(res?.data?.tasks || []),
            ]);
          } else {
            setModuleTasks([...res.data.tasks]);
          }
        }
        return res;
      });
    }
  };

  /**
   * Fetches the count of module tasks.
   *
   * @param {string} projectId - The ID of the project.
   * @param {boolean} hideLoaderOnRequest - Whether to hide the loader on request.
   * @param {number} count - The count of tasks to fetch.
   * @return {Promise} A promise that resolves with the count of tasks.
   */
  const fetchModuleTasksCount = (projectId, hideLoaderOnRequest, count) => {
    if (projectId) {
      fetchModuleTasks(
        MODULE_SUB_MODULE_OPERATIONS_TREE.EDA.objKey,
        MODULE_SUB_MODULE_OPERATIONS_TREE.EDA.subModules.MVI.key,
        'GET_EDA_TASKS',
        () => {},
        projectId,
        hideLoaderOnRequest,
        '',
        null,
        null,
        null,
        count,
        MODULE_SUB_MODULE_OPERATIONS_TREE.EDA.key
      ).then((res) => {
        setTaskCount(res?.data);
      });
    }
  };

  /**
   * @function fetchMVIModuleTasksConsole
   * @description API call for fetching data quality module tasks
   * @param {number} projectId - module tasks of a particular project id
   * @param {boolean} hideLoaderOnRequest -hiding loader on request
   * @returns {undefined}
   * @example fetchMVIModuleTasksConsole(projectId,hideLoaderOnRequest);
   */
  const fetchMVIModuleTasksConsole = (
    projectId,
    hideLoaderOnRequest,
    pageIndex,
    pageSize,
    taskStatus
  ) => {
    if (projectId) {
      fetchModuleTasksConsole(
        MODULE_SUB_MODULE_OPERATIONS_TREE.EDA.objKey,
        MODULE_SUB_MODULE_OPERATIONS_TREE.EDA.subModules.MVI.key,
        'GET_EDA_TASKS',
        projectId,
        hideLoaderOnRequest,
        pageIndex,
        pageSize,
        taskStatus
      ).then((res) => {
        if (res.data) {
          setTotalModuleTaskConsole(res.headers[totalPageCount]);
          if (pageIndex > 1) {
            setConsoleModuleTasks([...consoleModuleTasks, ...res.data]);
          } else {
            setConsoleModuleTasks([...res.data]);
          }
        }
      });
    }
  };

  /**
   * @function fetchAsyncResultConsoleAndActivateDataset
   * @description API call for fetching asynchronous result
   * @param {number}  taskID - task id
   * @param {object}  additionTaskDetails - addition task details
   * @returns  {Promise} AxiosPromise
   * @example  fetchAsyncResultConsoleAndActivateDataset(taskID, additionTaskDetails)
   */
  const fetchAsyncResultConsoleAndActivateDataset = (
    taskID,
    additionTaskDetails
  ) =>
    fetchAsyncResultConsole(taskID, additionTaskDetails).then(
      (res) => {
        if (res.data.task_status === 'SUCCESS') {
          if (utils.isNewDatasetCreatedByUdf(res.data)) {
            fetchDatasetList(
              project.activeProject?.project_id,
              true,
              [DATASET_REQUIRED_FIELDS].join()
            );
          }
          if (res.data?.parameters?.table) {
            datasetList.forEach((dataset) => {
              if (dataset.Name === res.data.parameters.table) {
                setActiveDataset({ ...dataset });
              }
            });
          }
        }
        return res;
      },
      (error) => {
        throw error;
      }
    );

  /**
   * @function fetchAsyncResult
   * @description API call for fetching asynchronous result
   * @param {string}  taskID - task id of the particular task
   * @param {function} successCallback - callback executed when promise get resolved
   * @returns {Promise} AxiosPromise
   * @example fetchAsyncResult(taskID, additionTaskDetails, successCallback)
   */

  // Fetch Asnyc Operation Result
  const fetchAsyncResult = (taskID, additionTaskDetails, successCallback) =>
    axios.get(`${BASE_URL + ApiEndpoints.EDA.GET_EDA_TASKS}/${taskID}`).then(
      (res) => {
        if (res.data.task_status === 'SUCCESS') {
          fetchDatasetList(
            project?.activeProject?.project_id,
            true,
            [DATASET_REQUIRED_FIELDS].join()
          );
          Object.values(
            MODULE_SUB_MODULE_OPERATIONS_TREE.EDA.subModules.MVI.operations
          ).filter((data) => {
            if (data.key === additionTaskDetails.operation) {
              if (res.data?.parameters?.dataset_id) {
                datasetList.forEach((dataset) => {
                  if (dataset.id === Number(res.data.parameters.dataset_id)) {
                    setActiveDataset(dataset);
                  }
                });
              }
              updateTaskDetails(res.data);
              setActiveOperation(data);
              successCallback(res.data);
            }
            return null;
          });
        } else {
          updateTaskDetails({});
        }
      },
      () => {}
    );

  /**
   * @function  terminateTask
   * @description API call for deleting a particular task
   * @param {string}  taskID - task id of the task we want to delete
   * @param {function} successCallback - callback executed when promise get resolved
   * @param {function} errorCallback - callback executed when promise get failed
   * @returns  {Promise} AxiosPromise
   * @example  terminateTask(taskID, successCallback, errorCallback)
   */

  // For Terminating Task in Task Manager
  const terminateTask = (taskID) =>
    axios
      .delete(`${BASE_URL + ApiEndpoints.EDA.GET_EDA_TASKS}/${taskID}`)
      .then((res) => {
        return res;
      })
      .catch((e) => {
        throw e;
      });

  /**
   * @function ftsRerunOperation
   * @description API call for reruning the operation
   * @param {object} body - body
   * @returns {Promise} AxiosPromise
   * @example ftsRerunOperation(body);
   */
  const edaRerunOperation = (body) => {
    return axios
      .post(`${BASE_URL + ApiEndpoints.EDA.RERUN}`, body)
      .then((res) => {
        return res;
      })
      .catch((e) => {
        throw e;
      });
  };

  /**
   * @function missingValuesSubmitOp
   * @description function to perform missing Value imputation operation
   * @param {object} body - object containing details of operation performed
   * @param {function} successCallback - callback executed when promise get resolved
   * @param {function} errorCallback - callback executed when promise get failed
   * @returns {undefined}
   * @example missingValuesSubmitOp(body, successCallback, errorCallback)
   */

  // For Submit Op of Missing Values
  const missingValuesSubmitOp = (body, successCallback) =>
    axios
      .post(`${BASE_URL + ApiEndpoints.EDA.EDA_MVI_SUBMIT}`, {
        ...body,
        action: 'submit',
      })
      .then(
        () => {
          if (taskDetails?.results) {
            updateTaskDetails({
              ...taskDetails,
              results: {},
            });
          }
          successCallback();
        },
        (error) => {
          if (taskDetails?.results) {
            updateTaskDetails({
              ...taskDetails,
              results: {},
            });
          }
          throw error;
        }
      );

  /**
   * @function missingValuesSavetOp
   * @description function to save the result of missing Value imputation operation
   * @param {object} body - object containing details of operation performed
   * @param {function} successCallback - callback executed when promise get resolved
   * @param {function} errorCallback - callback executed when promise get failed
   * @returns {undefined}
   * @example missingValuesSaveOp(body, successCallback, errorCallback)
   */

  // For Save Op of Missing Values
  const missingValuesSaveOp = (body, successCallback) =>
    axios
      .post(`${BASE_URL + ApiEndpoints.EDA.EDA_MVI_SAVE}`, {
        ...body,
        action: 'save',
      })
      .then(
        () => {
          if (taskDetails?.results) {
            updateTaskDetails({
              ...taskDetails,
              results: {},
            });
          }
          successCallback();
        },
        (error) => {
          if (taskDetails?.results) {
            updateTaskDetails({
              ...taskDetails,
              results: {},
            });
          }
          throw error;
        }
      );

  /**
	@function markTaskReadUnreadVariablAnalysis
	@description API call to change the read status of the task in job queue section
	@param {object} taskParams - object containing detail of task like project id, section name etc
	@param {function} successCallback - callback executed when promise get resolved
	@param {function} errorCallback - callback executed when promise get failed
	@returns Axios promise
	@example markTaskReadUnreadVariablAnalysis(taskParams, successCallback, errorCallback)
	*/

  const markTaskReadUnreadMissingValuesImputation = (
    taskParams,
    successCallback,
    errorCallback = () => {}
  ) =>
    axios
      .get(`${BASE_URL + ApiEndpoints.EDA.EDA_JOB_QUEUE_MARK_READ_UNREAD}`, {
        params: taskParams,
      })
      .then(
        () => {
          successCallback();
        },
        () => {
          errorCallback();
        }
      );

  return {
    activeOperation,
    setActiveOperation,

    activeDataset,
    setActiveDataset,

    datasetColumnDetails,
    fetchDatasetColumnDetails,

    moduleTasks,
    setModuleTasks,
    totalModuleTask,
    fetchMVIModuleTasks,
    taskCount,
    fetchModuleTasksCount,

    resetTaskDetails,
    taskDetails,

    fetchAsyncResult,

    datasetList,
    fetchDatasetList,

    missingValuesSubmitOp,

    missingValuesSaveOp,

    terminateTask,
    edaRerunOperation,

    updateDatasetCells,

    fetchDatasetRowsWithPagination,
    datasetRowsWithPagination,
    setDatasetRowsWithPagination,
    totalDatasetRows,
    datasetRowsPageCount,
    isLoading,

    renameColumn,
    duplicateColumn,
    deleteColumn,
    addColumn,
    deleteRow,
    duplicateRow,
    addRow,
    gridTasks,
    gridTaskCount,
    fetchGridTasks,
    terminateGridTask,
    manageSpaces,
    manageCases,
    splitColumn,
    findAndReplace,
    sortColumn,
    markTaskReadUnreadMissingValuesImputation,
    analyse,
    embeddedValues,
    standardisationError,
    integrityRules,
    uniqueness,
    scores,
    fetchGridTaskData,
    project,
    findDuplicateRows,
    findDuplicateColumns,
    findAndDeleteDuplicateRows,
    findAndDeleteDuplicateColumns,
    setGridTaskResultsData,
    gridTaskResultsData,
    resetGridTaskResultsData,
    markTaskReadUnreadGrid,
    valueError,
    findBlankValuePercentage,
    replaceBlankValues,
    findRegexPercentage,
    gridOperationRerun,

    // console related
    consoleModuleTasks,
    totalModuleTaskConsole,
    consoleAsyncTaskResults,
    fetchAsyncResultConsoleAndActivateDataset,
    terminateTaskConsole,
    markTaskReadUnreadConsole,
    fetchMVIModuleTasksConsole,
    fetchUdfs,
    deleteUdf,
    createUdf,
    createJob,
    updateUdf,
    udfs,
    selectedUdf,
    setSelectedUdf,
    runUdf,
    githubIntegration,
    fetchUdfDetails,
    fetchDatasetColumnDetailsForConsole,
    fetchAllModels,
    setConsoleAsyncTaskResults,
    fetchTableData,
    onDownloadCSV,
    fetchConsoleLibraries,
    consoleRerunOperation,
    markDatasetAsReadyToUse,
    fetchConsoleFileList,
    addResultToModelMetaData,
    fetchModelMetaDataSchema,
    fetchAsyncResultSplit,
  };
}
